import React, {Fragment, type ReactNode, useEffect, useRef} from 'react';

import ReactDOM from 'react-dom';
import {useDispatch} from 'react-redux';

import {type PermsMap} from 'app/current-user/types';
import {SPPage} from 'app/general/structures/SPPage';
import {showNotification} from 'app/toasts/slice';
import {reportUnexpectedState} from 'app/utils/errors';
import {sendSentryMessage} from 'app/utils/sentry';

type Props = {
  children: ReactNode | ((perms: PermsMap) => ReactNode);
};

export function WrapStaticContentPage({children}: Props) {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const dashboardContentRef = useRef<HTMLElement | null>(null);
  const dashboardContentOriginalParentRef = useRef<ParentNode | null>(null);

  function getDashboardContentRefs() {
    if (dashboardContentRef.current == null) {
      dashboardContentRef.current = document.getElementById(
        'static-page-content'
      );
    }

    if (
      dashboardContentRef.current != null &&
      dashboardContentOriginalParentRef.current == null
    ) {
      dashboardContentOriginalParentRef.current =
        dashboardContentRef.current.parentNode;
    }
  }

  function attachContentToParent() {
    if (
      dashboardContentOriginalParentRef.current != null &&
      dashboardContentRef.current != null
    ) {
      dashboardContentOriginalParentRef.current.append(
        dashboardContentRef.current
      );
      dashboardContentRef.current.style.display = 'none';
    }
  }

  function attachContentToContainer() {
    if (containerRef.current != null && containerRef.current.hasChildNodes()) {
      // Early return, cause the node was already attached
      return;
    }
    getDashboardContentRefs();

    if (containerRef.current != null && dashboardContentRef.current != null) {
      containerRef.current.append(dashboardContentRef.current);
      dashboardContentRef.current.style.display = 'block';
    }
  }

  useEffect(() => {
    attachContentToContainer();
    // `DOMContentLoaded` may fire before your script has a chance to run, so check before adding a listener
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => callInitPage());
    } else {
      // `DOMContentLoaded` already fired
      callInitPage();
    }

    return attachContentToParent;
  }, []);

  useEffect(() => {
    getDashboardContentRefs();
  }, [dashboardContentRef.current, dashboardContentOriginalParentRef.current]);

  useEffect(() => {
    if (containerRef.current != null) {
      attachContentToContainer();
    }
  }, [containerRef.current]);

  function showErrorAndSendToSentry(message: string, opts: any) {
    const formattedOpts = {
      sentryData: {},
      ...opts,
    };
    sendSentryMessage(formattedOpts.sentryData.message || message, {
      extra: formattedOpts.sentryData,
    });
    showNotificationWrapper(message, 'error');
  }

  function renderChildren(userRoles: PermsMap) {
    if (children == null) {
      return null;
    }

    const placeholder = document.getElementById(
      'static-page-children-placeholder'
    );

    if (placeholder == null) {
      reportUnexpectedState('USEC13');
      return null;
    }

    return ReactDOM.createPortal(
      typeof children === 'function' ? children(userRoles) : children,
      placeholder
    );
  }

  function showNotificationWrapper(
    ...args: Parameters<typeof showNotification>
  ) {
    dispatch(showNotification(...args));
  }

  function callInitPage() {
    // @ts-expect-error initPageJS is missing from window declaration, but it might be there.
    const initPageJS = window.initPageJS || null;

    if (initPageJS != null && typeof initPageJS === 'function') {
      initPageJS(showNotificationWrapper, showErrorAndSendToSentry);
    }
  }

  return (
    <SPPage horizontalPadding={false}>
      {(userRoles) => (
        <Fragment>
          <div ref={containerRef} />
          {renderChildren(userRoles)}
        </Fragment>
      )}
    </SPPage>
  );
}
